import type { ShapeNameConversionDisplayMap } from '@mbfab/shared'
import { convertShapeNameForDisplay, isSome } from '@mbfab/shared'
import { chunk, sortBy } from 'es-toolkit'
import { FileSpreadsheet, FileText, Printer } from 'lucide-react'
import type { Dispatch, ReactNode, SetStateAction } from 'react'
import { Fragment, useRef, useState } from 'react'
import { useReactToPrint } from 'react-to-print'
import { z } from 'zod'

import type { SteelOrderExportPreviewColumn, SteelOrderExportRow } from './print/export-download'
import {
  buildSteelOrderExportRows,
  csvExportPreviewColumns,
  downloadSteelOrderCsv,
  excelExportPreviewColumns,
  groupSteelOrderExportRowsByConstruction,
} from './print/export-download'
import {
  EXPORT_REORDERABLE_COLUMN_TYPES,
  fixColumnOrderFor,
  fixExportColumnOrderFor,
  initialColumnConfigs,
  initialExportColumnConfigs,
  printColumnTypeLabel,
  REORDERABLE_COLUMN_TYPES,
  restoredExportColumnConfigsSchema,
  SLICE_NUMBER,
  SUMMARY_COLUMN_COUNT_WARNING_THRESHOLD,
} from './print/print-columns'
import { PrintPlannedPage } from './print/print-s3-object'
import { pageSize, pageStyle } from './print/print-style'
import type { HeaderLabelTextConfig } from './print/svg/header'
import {
  DEFAULT_CONSTRUCTION_EXECUTOR,
  DEFAULT_CONSTRUCTION_MANAGER,
  DEFAULT_SUMMARY_TITLE,
  headerLabelTextConfigSchema,
} from './print/svg/header'
import type { ColumnConfig } from './print/svg/main-row'
import { columnConfigSchema } from './print/svg/main-row'
import type { Item } from './print/svg/millsheet-list-svg'
import { MillsheetListSvgV2 } from './print/svg/millsheet-list-svg'
import type { MillsheetForPrint, PrintDialogState, S3ObjectForPrint, SteelOrderForPrint } from './print-types'

import { PrintColumnSettingsSection, reorderColumnConfigs } from '@/components/app/print-column-settings'
import type { PrintDialogTab } from '@/components/app/print-dialog-shell'
import {
  PrintDetailsSection,
  PrintDialogField,
  PrintDialogFooter,
  PrintDialogPreviewPane,
  PrintDialogPreviewToolbar,
  PrintDialogSettingsBody,
  PrintDialogSettingsPane,
  PrintDialogShell,
  PrintDialogStatusPanes,
  PrintDialogTabNav,
  PrintExportCard,
  PrintExportPreviewPane,
  PrintTargetRow,
  usePrintPreviewZoom,
} from '@/components/app/print-dialog-shell'
import {
  buildPrintPagePlan,
  buildPrintPagePlanSources,
  buildSummaryPageLabels,
  findFirstMillsheetPageNo,
  hasPrintableMillsheetPage,
} from '@/components/app/print-page-plan'
import { downloadMillsheetListExcel } from '@/components/screens/millsheet/print/excel-download'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { DataTable } from '@/components/ui/data-table/data-table'
import type { DataTableColumn } from '@/components/ui/data-table/data-table'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { APP_LOCAL_STORAGE_KEYS } from '@/lib/local-storage-keys'
import { notify } from '@/lib/notify'
import { RETRY_HINT } from '@/lib/run-mutation'
import { useLocalStorageState } from '@/lib/use-local-storage-state'

//
// 発注照合「印刷・データ出力」ダイアログ。
// v1 apps/frontend/client/src/components/orderMatching/SteelOrderMillsheetPrintForm.tsx の移植（PR E）。
// v1 は自前で fetchSteelOrdersForPrint / fetchS3Objects を呼ぶが、v2 は「画面コンポーネントは props 駆動・
// GraphQL は route 側」の方針のため、データ取得は OrdersTable の「印刷」ボタン押下時（route から注入された
// フェッチャーを呼ぶ）で完結させ、このコンポーネントは取得結果（PrintDialogState）を props で受け取るだけにする。
// Excel / CSV 出力も、印刷と同じ取得結果を使って対象のずれを防ぐ。
//

// 印刷対象1件（発注 × ミルシート）
type PrintMillsheetEntry = {
  referenceNumber: string
  supplierName: string | null
  millsheet: MillsheetForPrint
}

const printTargetFlagsSchema = z.object({
  summary: z.boolean(),
  original: z.boolean(),
  attachments: z.boolean(),
})
type PrintTargetFlags = z.infer<typeof printTargetFlagsSchema>

export function PrintDialog({
  open,
  state,
  selectedCount,
  shapeNameConversionMap,
  onClose,
  excelExporter = downloadMillsheetListExcel,
}: {
  open: boolean
  state: PrintDialogState
  selectedCount: number
  shapeNameConversionMap: ShapeNameConversionDisplayMap
  onClose: () => void
  // 「使用鋼材一覧表」Excel の実ダウンロード処理。既定は実実装（downloadMillsheetListExcel）で、
  // Storybook の複数ファイル確認モーダル story だけモック関数に差し替える（exceljs/file-saver を
  // 実行させずに「確認後に実ダウンロード処理が呼ばれること」だけを検証するため）。
  // プロダクションの呼び出し元（OrdersTable 等）は指定不要
  excelExporter?: typeof downloadMillsheetListExcel
}) {
  // OutputDateDialog / ConfirmDialog と同じく、閉じている間は丸ごとアンマウントして
  // タブ・ズーム以外の一時状態を持ち越さない（次に開いたときは常に新しい取得結果から出発する）
  if (!open) {
    return null
  }
  return (
    <PrintDialogContent
      state={state}
      selectedCount={selectedCount}
      shapeNameConversionMap={shapeNameConversionMap}
      onClose={onClose}
      excelExporter={excelExporter}
    />
  )
}

function PrintDialogContent({
  state,
  selectedCount,
  shapeNameConversionMap,
  onClose,
  excelExporter,
}: {
  state: PrintDialogState
  selectedCount: number
  shapeNameConversionMap: ShapeNameConversionDisplayMap
  onClose: () => void
  excelExporter: typeof downloadMillsheetListExcel
}) {
  const [tab, setTab] = useState<PrintDialogTab>('print')
  const [printTargetFlags, setPrintTargetFlags] = useLocalStorageState<PrintTargetFlags>(
    APP_LOCAL_STORAGE_KEYS.printTargetFlags,
    { summary: true, original: true, attachments: true },
    printTargetFlagsSchema,
  )
  return (
    <PrintDialogShell description={<>選択した {selectedCount} 件を対象にしています</>} onClose={onClose}>
      {tab === 'print' ? (
        <PrintTabPanes
          tab={tab}
          onChangeTab={setTab}
          state={state}
          shapeNameConversionMap={shapeNameConversionMap}
          printTargetFlags={printTargetFlags}
          setPrintTargetFlags={setPrintTargetFlags}
        />
      ) : (
        <ExportTabPanes
          tab={tab}
          onChangeTab={setTab}
          orders={state.status === 'ready' ? state.orders : []}
          images={state.status === 'ready' ? state.images : []}
          shapeNameConversionMap={shapeNameConversionMap}
          printTargetFlags={printTargetFlags}
          excelExporter={excelExporter}
        />
      )}
    </PrintDialogShell>
  )
}

// 「印刷設定」タブの中身（左の設定ペイン + 右のプレビュー）を取得状態（loading/error/ready）で出し分ける。
// PrintDialogContent の grid の直接の子として2要素（左ペインdiv・右asideのプレビュー）を返す必要があるため、
// 単一の DOM ノードを作らない React.Fragment で包む（Fragment はレンダーされず、中の2要素がそのまま
// grid の直接の子になる）。ready 時の状態（印刷する内容・列設定等）は左右のペインで共有する必要があるため、
// この関数を2回呼ぶ（左右で別コンポーネントにする）のではなく、1つのコンポーネントが2要素をまとめて返す
function PrintTabPanes({
  tab,
  onChangeTab,
  state,
  shapeNameConversionMap,
  printTargetFlags,
  setPrintTargetFlags,
}: {
  tab: PrintDialogTab
  onChangeTab: (tab: PrintDialogTab) => void
  state: PrintDialogState
  shapeNameConversionMap: ShapeNameConversionDisplayMap
  printTargetFlags: PrintTargetFlags
  setPrintTargetFlags: Dispatch<SetStateAction<PrintTargetFlags>>
}) {
  if (state.status === 'closed') {
    // PrintDialog が open のときしかこのコンポーネントは描画されないため実際には到達しない分岐だが、
    // PrintDialogState を網羅的に扱うために残す
    return null
  }
  if (state.status === 'loading') {
    return (
      <PrintDialogStatusPanes
        status="loading"
        loadingTestId="steel-order-print-loading"
        errorTestId="steel-order-print-error"
        loadingMessage="発注データを読み込み中です…"
        settingsHeader={<PrintDialogTabNav tab={tab} onChangeTab={onChangeTab} />}
      />
    )
  }
  if (state.status === 'error') {
    return (
      <PrintDialogStatusPanes
        status="error"
        loadingTestId="steel-order-print-loading"
        errorTestId="steel-order-print-error"
        settingsHeader={<PrintDialogTabNav tab={tab} onChangeTab={onChangeTab} />}
      />
    )
  }
  return (
    <PrintTabPanesReady
      tab={tab}
      onChangeTab={onChangeTab}
      orders={state.orders}
      images={state.images}
      shapeNameConversionMap={shapeNameConversionMap}
      printTargetFlags={printTargetFlags}
      setPrintTargetFlags={setPrintTargetFlags}
    />
  )
}

function PrintTabPanesReady({
  tab,
  onChangeTab,
  orders,
  images,
  shapeNameConversionMap,
  printTargetFlags,
  setPrintTargetFlags,
}: {
  tab: PrintDialogTab
  onChangeTab: (tab: PrintDialogTab) => void
  orders: SteelOrderForPrint[]
  images: S3ObjectForPrint[]
  shapeNameConversionMap: ShapeNameConversionDisplayMap
  printTargetFlags: PrintTargetFlags
  setPrintTargetFlags: Dispatch<SetStateAction<PrintTargetFlags>>
}) {
  const componentRef = useRef<HTMLDivElement>(null)
  const { viewportRef, zoom, ...zoomControls } = usePrintPreviewZoom()
  const [detailsOpen, setDetailsOpen] = useState(false)

  // 印刷する内容（集計表 / ミルシート原本 / 添付資料）。v1 と同じキーで localStorage に保存する
  // （client は別ポート・別オリジンで動くため v1 と保存領域が競合することはない。将来 v2 が
  // v1 を置き換えたときに同じ利用者の設定を引き継げるよう、キー名は揃えている）
  const [columnConfigs, setColumnConfigs] = useLocalStorageState<ColumnConfig[]>(
    APP_LOCAL_STORAGE_KEYS.summaryColumnConfigs,
    initialColumnConfigs(),
    columnConfigSchema.array(),
  )
  const [headerLabelTextConfig, setHeaderLabelTextConfig] = useLocalStorageState<HeaderLabelTextConfig>(
    APP_LOCAL_STORAGE_KEYS.headerLabelTextConfig,
    {},
    headerLabelTextConfigSchema,
  )

  const printMillsheets: PrintMillsheetEntry[] = orders.flatMap((order) =>
    sortBy(order.millsheets, [(x) => x.no]).map((millsheet) => ({
      referenceNumber: order.referenceNumber,
      supplierName: order.supplierName,
      millsheet,
    })),
  )

  const pageSources = buildPrintPagePlanSources({
    millsheets: printMillsheets.map((entry) => entry.millsheet),
    images,
  })

  const pagePlan = buildPrintPagePlan({
    millsheets: printMillsheets.map((entry) => ({
      id: entry.millsheet.id,
      constructionName: entry.millsheet.constructionName,
    })),
    sources: pageSources,
    targets: printTargetFlags,
  })
  const orderedMillsheets = pagePlan.orderedMillsheets.flatMap((plannedMillsheet) =>
    printMillsheets.filter((entry) => entry.millsheet.id === plannedMillsheet.id),
  )
  const printPages = orderedMillsheets.map((entry) => {
    const mainNumber = findFirstMillsheetPageNo(pagePlan.pages, entry.millsheet.id)
    const rowLabels = buildSummaryPageLabels({
      pageNo: mainNumber,
      itemCount: entry.millsheet.millsheetItems.length,
      hasPrintableOriginal: hasPrintableMillsheetPage(pageSources, entry.millsheet.id),
      originalSelected: printTargetFlags.original,
    })
    const detailItems = entry.millsheet.millsheetItems.length === 0 ? [undefined] : entry.millsheet.millsheetItems
    return {
      entry,
      items: detailItems.map((item, itemIndex): Item => ({
        // ミルシート側の品名・サイズ・長さは変換マスタで発注データ側の表記へ名寄せして表示（表示のみ）
        sectionType: isSome(item)
          ? convertShapeNameForDisplay(shapeNameConversionMap, 'productName', item.sectionType)
          : '',
        steelType: item?.steelType ?? '',
        size: isSome(item) ? convertShapeNameForDisplay(shapeNameConversionMap, 'size', item.size) : '',
        thicknessDiameter: item?.thicknessDiameter ?? null,
        length: isSome(item) ? convertShapeNameForDisplay(shapeNameConversionMap, 'length', item.length) : '',
        quantity: item?.quantity.toString() ?? '',
        weight: item?.weightBeforeProcessing?.toString() ?? '',
        heatNumber: item?.heatNumber ?? '',
        individualNumber: item?.individualNumber ?? '',
        no: entry.millsheet.no,
        manufacturerName: entry.millsheet.manufacturerName,
        // 工区・節・部位は明細ごとの属性（親ミルシートの値は集約表示のため使わない）
        constructionArea: item?.constructionArea ?? '',
        member: item?.member ?? '',
        buildingPart: item?.buildingPart ?? '',
        memo: '',
        pageNumber: rowLabels[itemIndex] ?? '',
        tradingCompany: entry.millsheet.tradingCompany,
        orderNumber: entry.millsheet.orderNumber,
        innerNo: entry.millsheet.innerNo,
        endorsementCompany: item?.endorsementCompany ?? null,
        customer: entry.millsheet.customer,
        referenceNumber: entry.referenceNumber,
        supplierName: entry.supplierName,
      })),
    }
  })
  // 工事名称ごとに「集計表 →（その工事の）原本PDF」の順で出力する
  const constructionNames = Array.from(new Set(printPages.map((page) => page.entry.millsheet.constructionName)))
  const printGroups = constructionNames.map((constructionName) =>
    printPages.filter((page) => page.entry.millsheet.constructionName === constructionName),
  )
  const totalItemCount = printPages.reduce((acc, x) => acc + x.items.length, 0)
  const activeColumnConfigs = fixColumnOrderFor(columnConfigs)

  const handlePrint = useReactToPrint({
    pageStyle,
    onBeforePrint: async () => {
      // ブラウザ印刷時の PDF タイトルになるため設定しておく（v1 と同じ手法）
      document.title = DEFAULT_SUMMARY_TITLE
    },
    contentRef: componentRef,
  })

  const summaryPageCount = printTargetFlags.summary
    ? printGroups.reduce(
        (acc, groupPages) =>
          acc +
          chunk(
            groupPages.flatMap((x) => x.items),
            SLICE_NUMBER,
          ).length,
        0,
      )
    : 0
  const totalPageCount = summaryPageCount + pagePlan.pages.length
  const selectedTargetCount = [
    printTargetFlags.summary,
    printTargetFlags.original,
    printTargetFlags.attachments,
  ].filter(Boolean).length

  const summaryColumnCountExceeded = activeColumnConfigs.length >= SUMMARY_COLUMN_COUNT_WARNING_THRESHOLD

  const toggleColumn = (type: ColumnConfig['type']) => {
    setColumnConfigs((prev) => prev.map((x) => (x.type === type ? { ...x, active: !x.active } : x)))
  }

  return (
    <>
      <PrintDialogSettingsPane>
        <PrintDialogTabNav tab={tab} onChangeTab={onChangeTab} />
        <PrintDialogSettingsBody>
          <fieldset>
            <legend className="text-sm font-semibold text-foreground">印刷する内容</legend>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">必要な帳票だけを選択してください。</p>
            <div className="mt-3 flex flex-col overflow-hidden rounded-lg border border-border">
              <PrintTargetRow
                index={0}
                checked={printTargetFlags.summary}
                onChange={(checked) => {
                  setPrintTargetFlags((x) => ({ ...x, summary: checked }))
                }}
                label="鋼材ミルシート集計表"
                description="選択した発注をまとめた一覧表"
                details={
                  <PrintDetailsSection
                    open={detailsOpen}
                    onToggleOpen={() => {
                      setDetailsOpen((value) => !value)
                    }}
                    testId="steel-order-summary-details-toggle"
                  >
                    <fieldset>
                      <legend className="text-xs font-semibold text-foreground">帳票タイトル・記載情報</legend>
                      <div className="mt-3 flex flex-col gap-3">
                        <PrintDialogField
                          label="タイトル（集計表）"
                          value={headerLabelTextConfig.summaryTitle ?? ''}
                          placeholder={DEFAULT_SUMMARY_TITLE}
                          onChange={(value) => {
                            setHeaderLabelTextConfig((prev) => ({ ...prev, summaryTitle: value }))
                          }}
                        />
                        <PrintDialogField
                          label="工事監理者"
                          multiline
                          value={headerLabelTextConfig.constructionManager ?? ''}
                          placeholder={`${DEFAULT_CONSTRUCTION_MANAGER}（2行まで入力できます）`}
                          onChange={(value) => {
                            setHeaderLabelTextConfig((prev) => ({ ...prev, constructionManager: value }))
                          }}
                        />
                        <PrintDialogField
                          label="工事施行者"
                          multiline
                          value={headerLabelTextConfig.constructionExecutor ?? ''}
                          placeholder={`${DEFAULT_CONSTRUCTION_EXECUTOR}（2行まで入力できます）`}
                          onChange={(value) => {
                            setHeaderLabelTextConfig((prev) => ({ ...prev, constructionExecutor: value }))
                          }}
                        />
                      </div>
                    </fieldset>
                    <PrintColumnSettingsSection
                      title="集計表の表示項目"
                      subtitle={`${activeColumnConfigs.filter((x) => x.type !== 'pageNumber').length}項目`}
                      testIdPrefix="steel-order-print-columns"
                      open
                      columnConfigs={columnConfigs}
                      labels={printColumnTypeLabel}
                      reorderableTypes={REORDERABLE_COLUMN_TYPES}
                      onToggleColumn={toggleColumn}
                      onReorder={(types) => {
                        setColumnConfigs((prev) => reorderColumnConfigs(prev, REORDERABLE_COLUMN_TYPES, types))
                      }}
                    />
                    {summaryColumnCountExceeded && (
                      <Callout tone="warning" testId="steel-order-summary-column-warning">
                        項目数が多いとレイアウトが崩れることがあります（現在{activeColumnConfigs.length}
                        項目）。項目を増やすほど1列の幅が狭くなります。
                      </Callout>
                    )}
                  </PrintDetailsSection>
                }
              />
              <PrintTargetRow
                index={1}
                checked={printTargetFlags.original}
                onChange={(checked) => {
                  setPrintTargetFlags((x) => ({ ...x, original: checked }))
                }}
                label="ミルシート"
                description="各製造会社の証明書PDF"
              />
              <PrintTargetRow
                index={2}
                checked={printTargetFlags.attachments}
                onChange={(checked) => {
                  setPrintTargetFlags((x) => ({ ...x, attachments: checked }))
                }}
                label="添付資料"
                description="登録済みの関連資料"
              />
            </div>
          </fieldset>
        </PrintDialogSettingsBody>

        <PrintDialogFooter
          summary={
            totalItemCount === 0
              ? '紐付いているミルシートがありません'
              : `${selectedTargetCount}種類 / 全${totalPageCount}ページを印刷`
          }
        >
          <Button
            className="gap-2 px-4"
            data-testid="steel-order-print-execute"
            disabled={totalItemCount === 0 || selectedTargetCount === 0}
            onClick={() => {
              handlePrint()
            }}
          >
            <Printer className="size-4" />
            印刷する
          </Button>
        </PrintDialogFooter>
      </PrintDialogSettingsPane>

      {/* 右ペイン: プレビュー。PrintTabPanesReady が Fragment で左ペイン(上のdiv)と一緒に返す
          ことで、grid の直接の子として左ペインの隣に並ぶ（PrintTabPanes 冒頭のコメント参照） */}
      <PrintDialogPreviewPane>
        <PrintDialogPreviewToolbar pageCount={totalPageCount} zoom={zoomControls} />
        {totalItemCount === 0 ? (
          <div
            className="flex flex-1 items-center justify-center p-8 text-center text-sm text-muted-foreground"
            data-testid="steel-order-print-empty"
          >
            選択した発注に紐付いているミルシートがありません（未登録の発注は印刷できません）。
          </div>
        ) : (
          <div
            ref={viewportRef}
            className="min-h-0 flex-1 overflow-auto p-5 lg:p-7"
            data-testid="steel-order-print-preview"
          >
            {/* 拡大縮小は componentRef の「外側」に掛ける。react-to-print は componentRef のサブツリーだけを
                複製するため、ここの倍率は印刷結果に影響しない */}
            <div className="mx-auto bg-white shadow-md" style={{ width: pageSize.width, zoom }}>
              <div ref={componentRef}>
                {printGroups.map((groupPages) => {
                  const constructionName = groupPages[0]?.entry.millsheet.constructionName ?? ''
                  const groupItems = groupPages.flatMap((x) => x.items)
                  return (
                    <Fragment key={constructionName}>
                      {printTargetFlags.summary &&
                        chunk(groupItems, SLICE_NUMBER).map((pageItems, pageIndex) => (
                          <div
                            key={pageIndex}
                            data-testid="print-summary-page"
                            data-construction-name={constructionName}
                          >
                            <MillsheetListSvgV2
                              sumKg=""
                              headerLabelTextConfig={headerLabelTextConfig}
                              columnConfigs={activeColumnConfigs}
                              indexStart={1 + pageIndex * SLICE_NUMBER}
                              date=""
                              constructionName={constructionName}
                              items={pageItems}
                              asSummary
                            />
                          </div>
                        ))}
                      {pagePlan.pages
                        .filter((page) => groupPages.some(({ entry }) => entry.millsheet.id === page.millsheetId))
                        .map((page) => (
                          <PrintPlannedPage key={`${page.s3ObjectId}-${page.sourcePageIndex}`} page={page} />
                        ))}
                    </Fragment>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </PrintDialogPreviewPane>
    </>
  )
}

// 選択した発注にミルシートが紐付いていないため出力データが1行も無いときの案内文。
// 印刷タブの空状態（steel-order-print-empty）と文言のトーンを揃える
const EXPORT_DISABLED_REASON =
  '選択した発注にミルシートが紐付いていないため、ダウンロードできません。発注にミルシートを紐付けてから、もう一度お試しください。'

const EXPORT_PREVIEW_ROW_LIMIT = 50
const CSV_PREVIEW_NOTE = '実際にダウンロードされるデータと同じ列・同じ値のプレビューです（罫線等の書式は含みません）。'

function excelPreviewNote(rows: SteelOrderExportRow[]): string {
  const fileCount = groupSteelOrderExportRowsByConstruction(rows).length
  return `実際にダウンロードされるデータと同じ列・同じ値のプレビューです（罫線等の書式は含みません）。実際の Excel は工事名称ごとに${fileCount}ファイルに分かれ、タイトル・物件名の行や、形状ごとの小計・合計行が追加されます（このプレビューには含みません）。`
}

// 「データ出力」タブの中身（左の設定ペイン + 右の擬似プレビュー）。印刷タブの PrintTabPanesReady と
// 同じ理由（行データ・列設定を左右のペインで共有する必要がある）で、Fragment で2要素をまとめて返す。
// ページ番号は印刷タブと同じ buildPrintPagePlan から採番し、印刷される集計表・添付資料の物理ページ順と
// Excel/CSV のページ番号がずれないようにする（PR #312 print-page-plan.ts 参照）
function ExportTabPanes({
  tab,
  onChangeTab,
  orders,
  images,
  shapeNameConversionMap,
  printTargetFlags,
  excelExporter,
}: {
  tab: PrintDialogTab
  onChangeTab: (tab: PrintDialogTab) => void
  orders: SteelOrderForPrint[]
  images: S3ObjectForPrint[]
  shapeNameConversionMap: ShapeNameConversionDisplayMap
  printTargetFlags: PrintTargetFlags
  excelExporter: typeof downloadMillsheetListExcel
}) {
  const [exportColumnConfigs, setExportColumnConfigs] = useLocalStorageState<ColumnConfig[]>(
    APP_LOCAL_STORAGE_KEYS.steelOrderExcelColumnConfigs,
    initialExportColumnConfigs(),
    restoredExportColumnConfigsSchema,
  )
  // 複数の工事名称にまたがる Excel ダウンロードは、1回のクリックから saveAs が複数回連続で呼ばれる
  // （下の runExcelDownload 参照）。ブラウザの「複数ファイルのダウンロード許可」を挟む前に
  // 事前説明モーダルを出すための保留状態（null=未表示 / 数値=表示中で保存されるファイル枚数）
  const [pendingExcelDownloadFileCount, setPendingExcelDownloadFileCount] = useState<number | null>(null)

  const exportMillsheets = orders.flatMap((order) => sortBy(order.millsheets, [(millsheet) => millsheet.no]))
  const exportPageSources = buildPrintPagePlanSources({
    millsheets: exportMillsheets,
    images,
  })
  const exportPagePlan = buildPrintPagePlan({
    millsheets: exportMillsheets.map((millsheet) => ({
      id: millsheet.id,
      constructionName: millsheet.constructionName,
    })),
    sources: exportPageSources,
    targets: printTargetFlags,
  })
  const exportPageNumberMap = new Map<string, number | string>(
    exportPagePlan.orderedMillsheets.map((millsheet) => {
      const pageNo = findFirstMillsheetPageNo(exportPagePlan.pages, millsheet.id)
      const pageLabel =
        buildSummaryPageLabels({
          pageNo,
          itemCount: 1,
          hasPrintableOriginal: hasPrintableMillsheetPage(exportPageSources, millsheet.id),
          originalSelected: printTargetFlags.original,
        })[0] ?? 'PDFなし'
      return [millsheet.id, pageLabel]
    }),
  )
  const [exportColumnsOpen, setExportColumnsOpen] = useState(false)
  const rows = buildSteelOrderExportRows(orders, shapeNameConversionMap, exportPageNumberMap)
  const excelColumns = fixExportColumnOrderFor(exportColumnConfigs)
  const disabled = rows.length === 0

  const runExcelDownload = async () => {
    // 工事名称ごとに1ファイルずつ出力する（要望: 物件名に単一の工事名称だけを入れたいため）。
    // ページ番号（主番-枝番）は選択発注全体を通した連番のままにする（印刷される集計表と
    // 番号を一致させるため、ファイルを分けても採番し直さない）。zip 化はせず、要望どおり
    // 複数の .xlsx をそれぞれ saveAs する
    try {
      for (const group of groupSteelOrderExportRowsByConstruction(rows)) {
        await excelExporter({
          items: group.rows.map((row) => row.item),
          constructionName: group.constructionName,
          columnConfigs: excelColumns,
          fileNameLabel: group.constructionName,
        })
      }
    } catch {
      // exceljs は明細が巨大だとメモリ不足等で例外を投げうる。呼び出し元（handleDownloadExcel /
      // ConfirmDialog の onConfirm の両方）はこの Promise を待たずに投げっぱなしにすることがあるため、
      // ここで捕捉してトーストを出す（docs/develop/client_ui/screens/states.md「操作の結果は「成功はトースト、失敗はトーストと画面の両方」」
      // の「mutation の手前で弾く分岐でも notify.error」と同じ理由。成功時はブラウザのダウンロードが可視なのでトーストは出さない）
      notify.error('Excelファイルのダウンロードに失敗しました', RETRY_HINT)
    }
  }

  const handleDownloadExcel = async () => {
    const groups = groupSteelOrderExportRowsByConstruction(rows)
    // Chrome/Edge は1回のユーザー操作から連続して複数ファイルを保存しようとすると「このサイトによる
    // 複数のファイルのダウンロードを許可しますか？」と確認し、ブロックされると2枚目以降が無言で
    // 保存されない。この許可・ブロックの結果は JS からは検知できないため、判定ロジックは作らず
    // 「事前に何が起きるか説明する」モーダルを挟むだけに留める。1ファイルで済むときは今までどおり即時ダウンロードする
    if (groups.length > 1) {
      setPendingExcelDownloadFileCount(groups.length)
      return
    }
    await runExcelDownload()
  }

  return (
    <>
      <PrintDialogSettingsPane>
        <PrintDialogTabNav tab={tab} onChangeTab={onChangeTab} />
        <ExportTab
          rows={rows}
          excelColumns={excelColumns}
          exportColumnConfigs={exportColumnConfigs}
          setExportColumnConfigs={setExportColumnConfigs}
          disabled={disabled}
          onDownloadExcel={handleDownloadExcel}
        />
        <ConfirmDialog
          open={isSome(pendingExcelDownloadFileCount)}
          title="Excelファイルのダウンロード"
          message={
            <>
              このダウンロードでは Excel ファイルを{pendingExcelDownloadFileCount}枚保存します。
              ブラウザから「複数のファイルのダウンロードを許可しますか？」と確認される場合があります。
              <strong className="font-bold">「許可」を選択してください。</strong>
              ブロックすると2枚目以降が保存されません。
            </>
          }
          tone="neutral"
          icon={<FileSpreadsheet className="size-4" />}
          confirmLabel="ダウンロードする"
          confirmTestId="steel-order-export-multi-file-confirm"
          onClose={() => {
            setPendingExcelDownloadFileCount(null)
          }}
          onConfirm={() => {
            setPendingExcelDownloadFileCount(null)
            void runExcelDownload()
          }}
        />
      </PrintDialogSettingsPane>
      <PrintExportPreviewPane />
    </>
  )
}

// 「データ出力」タブの左ペイン（設定・ダウンロードボタン）。印刷と同じ取得結果から作った行データを
// props で受け取るだけで、行データ自体の生成は ExportTabPanes 側（右ペインと共有するため）で行う
function ExportTab({
  rows,
  excelColumns,
  exportColumnConfigs,
  setExportColumnConfigs,
  disabled,
  onDownloadExcel,
}: {
  rows: SteelOrderExportRow[]
  excelColumns: ColumnConfig[]
  exportColumnConfigs: ColumnConfig[]
  setExportColumnConfigs: Dispatch<SetStateAction<ColumnConfig[]>>
  disabled: boolean
  onDownloadExcel: () => Promise<void>
}) {
  const [exportColumnsOpen, setExportColumnsOpen] = useState(false)
  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 lg:px-6">
      <div className="flex flex-col gap-5">
        <h3 className="text-sm font-semibold text-foreground">データをダウンロード</h3>
        {disabled && (
          <Callout tone="warning" testId="steel-order-export-disabled-reason">
            {EXPORT_DISABLED_REASON}
          </Callout>
        )}
        <PrintExportCard
          icon={<FileSpreadsheet className="size-5" />}
          title="使用鋼材一覧表"
          format="Excel（.xlsx）"
          description="一覧を表計算ソフトで編集・集計できます。工事名称ごとに 1 ファイルずつ出力します。"
          disabled={disabled}
          disabledReason={EXPORT_DISABLED_REASON}
          testId="steel-order-export-download-excel"
          onDownload={onDownloadExcel}
          detailsOpen={exportColumnsOpen}
          onToggleDetails={() => {
            setExportColumnsOpen((value) => !value)
          }}
          details={
            <PrintColumnSettingsSection
              title="Excelの表示項目"
              subtitle={`${excelColumns.filter((x) => x.type !== 'pageNumber').length}項目`}
              testIdPrefix="steel-order-print-excel-columns"
              open
              columnConfigs={exportColumnConfigs}
              labels={printColumnTypeLabel}
              reorderableTypes={EXPORT_REORDERABLE_COLUMN_TYPES}
              onToggleColumn={(type) => {
                setExportColumnConfigs((prev) => prev.map((x) => (x.type === type ? { ...x, active: !x.active } : x)))
              }}
              onReorder={(types) => {
                setExportColumnConfigs((prev) => reorderColumnConfigs(prev, EXPORT_REORDERABLE_COLUMN_TYPES, types))
              }}
            />
          }
        />
        <PrintExportCard
          icon={<FileText className="size-5" />}
          title="発注・ミルシートデータ"
          format="CSV（.csv）"
          description="選択行のデータを汎用形式で出力します。"
          disabled={disabled}
          disabledReason={EXPORT_DISABLED_REASON}
          testId="steel-order-export-download-csv"
          onDownload={async () => {
            try {
              await downloadSteelOrderCsv(rows)
            } catch {
              // Excel 側（runExcelDownload）と同じ理由でここも必ずトーストを出す。
              // 成功時はブラウザのダウンロードが可視なのでトーストは出さない
              notify.error('CSVファイルのダウンロードに失敗しました', RETRY_HINT)
            }
          }}
        />
      </div>
    </div>
  )
}

// 「データ出力」タブの右ペイン（擬似プレビュー）。Excel と CSV は列構成が異なる
// （Excel は「表示項目」で選んだ列だけ・CSV は常に固定16列）ため、共通化はせず
// 出力形式を切り替えて確認できるようにする
export function ExportPreviewPane({
  rows,
  excelColumns,
  disabled,
  previewFormat,
  onChangePreviewFormat,
}: {
  rows: SteelOrderExportRow[]
  excelColumns: ColumnConfig[]
  disabled: boolean
  previewFormat: 'excel' | 'csv'
  onChangePreviewFormat: (format: 'excel' | 'csv') => void
}) {
  return (
    <>
      <div className="flex h-12 shrink-0 items-center justify-between border-b border-border px-4">
        <span className="text-sm font-semibold text-foreground">プレビュー</span>
        {/* Excel/CSV の切り替えは PrintTabNav の「印刷設定/データ出力」と違い、別の内容（パネル）へ
            移動するタブではなく、同じプレビュー表の列・注記を切り替える択一の設定なので SegmentedControl にする */}
        {!disabled && (
          <SegmentedControl
            legend="形式"
            name="steel-order-export-preview-format"
            options={[
              { value: 'excel', label: 'Excel', testId: 'steel-order-export-preview-format-excel' },
              { value: 'csv', label: 'CSV', testId: 'steel-order-export-preview-format-csv' },
            ]}
            value={previewFormat}
            onChange={onChangePreviewFormat}
          />
        )}
      </div>
      {disabled ? (
        <div
          className="flex flex-1 items-center justify-center p-8 text-center text-sm text-muted-foreground"
          data-testid="steel-order-export-empty"
        >
          {EXPORT_DISABLED_REASON}
        </div>
      ) : (
        <ExportPreviewTable
          title={
            previewFormat === 'excel'
              ? '使用鋼材一覧表（Excel）のプレビュー'
              : '発注・ミルシートデータ（CSV）のプレビュー'
          }
          note={previewFormat === 'excel' ? excelPreviewNote(rows) : CSV_PREVIEW_NOTE}
          columns={previewFormat === 'excel' ? excelExportPreviewColumns(excelColumns) : csvExportPreviewColumns()}
          rows={rows}
        />
      )}
    </>
  )
}

// 実際のダウンロードと同じ行データ・列定義（export-download.ts の excelExportPreviewColumns /
// csvExportPreviewColumns）から組み立てた表をそのまま描画する。罫線・結合セルなど Excel 側の
// 書式までは再現しない（「どの列・どんな値が出力されるか」を確認できれば十分という前提）
function ExportPreviewTable({
  title,
  note,
  columns,
  rows,
}: {
  title: string
  note: string
  columns: SteelOrderExportPreviewColumn[]
  rows: SteelOrderExportRow[]
}) {
  const visibleRows = rows.slice(0, EXPORT_PREVIEW_ROW_LIMIT)
  const hiddenRowCount = rows.length - visibleRows.length
  return (
    <div className="min-h-0 flex-1 overflow-auto p-4" data-testid="steel-order-export-preview">
      <p className="mb-1 text-xs font-semibold text-foreground">{title}</p>
      <p className="mb-2 text-[11px] leading-4 text-muted-foreground">{note}</p>
      <DataTable
        testIdPrefix="steel-order-export-preview"
        // 出力行には ID が無く、並びも出力順で固定なので行番号をキーにする
        rows={visibleRows.map((row, index) => ({ row, index }))}
        rowKey={(item) => String(item.index)}
        columns={columns.map((column): DataTableColumn<{ row: SteelOrderExportRow; index: number }> => ({
          key: column.key,
          title: column.label,
          render: (item): ReactNode => column.getValue(item.row),
        }))}
      />
      {hiddenRowCount > 0 && (
        <p className="mt-2 text-[11px] text-muted-foreground" data-testid="steel-order-export-preview-truncated">
          他 {hiddenRowCount} 行（ダウンロードすると全 {rows.length} 行が出力されます）
        </p>
      )}
    </div>
  )
}
